import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import {
  ClientRelatedType,
  CreateAppointmentRequest,
  EmployeeResponse,
  LocationType,
  //MatchCandidateResponse,
  ProjectResponse,
  RecurrenceRangeTypeRequest,
  SchedulingLabel,
  UpdateAppointmentRequest,
  WorkPlaceResponse
} from 'generated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';
import { InferType } from 'yup';

import { Event } from '../../types';
import { OrganizationalUnit, PlanUnit } from 'components/types';

import { ViewContext } from '../../enums';

import {
  APNotification,
  appendCorrelationToken,
  getConflictingEvents,
  getViewContext,
  notificationsToHashTable
} from '../../utils';
import {
  getHoursAndMinutesFromSeconds,
  shouldDisableDateInRange
} from 'utils/date';
import { getCurrentlyLoggedInEmployee } from 'utils/misc';

import { mutations, queries } from 'RestAPIs/rapini';

import { dialogManagerActiveAtom, planUnitsAtom } from '../../atoms';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { showSnackbar } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import ExtraSection from './components/ExtraSection';
import WhatSection from './components/WhatSection';
import WhenSection from './components/WhenSection';
import WhereSection from './components/WhereSection';
import WhichSection from './components/WhichSection';
import WhoSection from './components/WhoSection';

import { DialogType } from '../DialogManager/DialogManager';
import appointmentSchema from './appointment-schema';
import { fieldGrants } from './input-field-grants';
import { getPlanUnits } from 'RestAPIs/cost-centers';

const schema = appointmentSchema();
type Form = InferType<typeof schema>;

const defaultPeriodType: 'WEEK' | 'DAY' = 'WEEK';
const defaultRecurrenceRangeType: RecurrenceRangeTypeRequest = 'NO_END';
const defaultLocationType: LocationType = 'CLIENTADDRESS';

const defaultValues = {
  activity: null,
  project: undefined,
  workPlace: undefined,
  //candidateEmployment: undefined,
  client: null,
  hourDuration: 0,
  minuteDuration: 0,
  employee: undefined,
  startDate: dayjs(),
  startTime: dayjs().startOf('day'),
  endTime: dayjs().startOf('day'),
  editAppointmentType: undefined,
  interval: 1,
  periodType: defaultPeriodType,
  dayInWeekSet: [format(new Date(), 'EEEE').toUpperCase()],
  recurrenceRangeType: defaultRecurrenceRangeType,
  description: '',
  locationType: defaultLocationType,
  employeeType: null
};

type Props = {
  appointment?: Event;
  schedulingLabel: SchedulingLabel;
  events?: Event[];

  handleClose: () => void;
};

export default function AddEditAppointmentForm({
  appointment: event,
  schedulingLabel,
  events,
  handleClose
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const isEditMode = event !== undefined;

  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);

  const [selectedProject, setSelectedProject] = useState<
    ProjectResponse | null | undefined
  >(null);

  const [selectedWorkPlace, setSelectedWorkPlace] = useState<
    WorkPlaceResponse | null | undefined
  >(null);

  const [, setPayload] = useState<UpdateAppointmentRequest | null>(null);
  const [defaultPlanUnit] = useRecoilValue(planUnitsAtom);

  const isVV = schedulingLabel === 'VV';
  const isWorkPlaceAppointment =
    schedulingLabel === 'WORKPLACE' && !event?.project; // Note: 'event?.project' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
  const isProjectAppointment =
    schedulingLabel === 'PROJECT' || !!event?.project; // Note: 'event?.project' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
  const isRecurring = !!event?.isRecurring;
  const context = getViewContext();
  const currentEmployee = getCurrentlyLoggedInEmployee();

  const methods = useForm<Form>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      organizationalUnit: event?.organizationalUnit
        ? event.organizationalUnit
        : defaultPlanUnit &&
          !('costCenters' in defaultPlanUnit) &&
          !(!!event?.project || schedulingLabel === 'PROJECT')
        ? defaultPlanUnit
        : undefined,
      isEditMode,
      isRecurring,
      schedulingLabel,
      hasProject: !!event?.project // Note: 'event?.project' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { dirtyFields }
  } = methods;

  const {
    activity,
    project,
    //candidateEmployment,
    client,
    dayInWeekSet,
    editAppointmentType,
    hourDuration,
    minuteDuration,
    locationType,
    periodType,
    isRecurring: isRecurringChecked,
    employee,
    startTime,
    startDate,
    employeeType
  } = watch();

  useEffect(() => {
    const endTime = dayjs(startTime)
      .add(hourDuration, 'hour')
      .add(minuteDuration, 'minute');
    setValue('endTime', endTime);
  }, [setValue, startTime, hourDuration, minuteDuration]);

  const clientRelatedTypes: ClientRelatedType[] | undefined =
    schedulingLabel === 'VV'
      ? client
        ? ['DIRECT_CLIENT_RELATED', 'INDIRECT_CLIENT_RELATED']
        : ['NON_CLIENT_RELATED']
      : undefined;

  const { data: activities } = queries.useGetActivities(
    //@ts-expect-error TODO: BE should accept array of ids
    schedulingLabel,
    clientRelatedTypes?.join(',')
  );

  const [organizationalUnits, setOrganizationalUnits] = useState<
    OrganizationalUnit[]
  >([]);
  const getOrganizationalUnits = async () => {
    const { data } = await getPlanUnits({
      startDate: dayjs().toDate(),
      endDate: dayjs().add(1, 'day').toDate(),
      type: 'employee'
    });
    setOrganizationalUnits(
      data.map(
        (planUnit: PlanUnit) =>
          ({ id: planUnit.id, name: planUnit.name }) as OrganizationalUnit
      )
    );
  };

  const { data: employeeTypes } = queries.useGetEmployeeTypes({
    enabled: isVV
  });
  const { data: clients } = queries.useGetClients(undefined, { enabled: isVV });

  const selectedOrganizationalUnit = watch(
    'organizationalUnit'
  ) as OrganizationalUnit;

  const hasFieldGrant = fieldGrants({
    // Dependencies passed to fieldGrants
    client,
    context,
    employee,
    isEditMode,
    isProjectAppointment,
    isRecurring,
    isRecurringChecked,
    isVV,
    isWorkPlaceAppointment,
    periodType
  });

  const handleEmployeeSelect = (
    employee: EmployeeResponse | undefined | null
  ) => {
    if (employee != null && project != null) {
      trigger('project');
    }
  };

  const handleProjectSelect = (project: ProjectResponse | undefined | null) => {
    if (!startDate || !hasFieldGrant('START_DATE_VALIDATION')) return;

    setSelectedProject(project);
    const dateRangeIsAllowed = !shouldDisableDateInRange(
      startDate,
      new Date(project?.validFrom || NaN),
      new Date(project?.validTo || NaN)
    );

    // Note: checking whether startDate falls within the accepted date range requires manual setting and triggering of the yup scheme
    setValue(
      'startDateIsValid',
      project?.validFrom ? dateRangeIsAllowed : true
    );
    trigger('startDate');
  };

  const handleWorkPlaceSelect = (
    workPlace: WorkPlaceResponse | undefined | null
  ) => {
    if (!startDate || !hasFieldGrant('START_DATE_VALIDATION')) return;

    setSelectedWorkPlace(workPlace);
    const dateRangeIsAllowed = !shouldDisableDateInRange(
      startDate,
      new Date(workPlace?.validFrom || NaN),
      new Date(workPlace?.validTo || NaN)
    );

    // Note: checking whether startDate falls within the accepted date range requires manual setting and triggering of the yup scheme
    setValue(
      'startDateIsValid',
      workPlace?.validFrom ? dateRangeIsAllowed : true
    );
    trigger('startDate');
  };

  useEffect(() => {
    handleProjectSelect(selectedProject);
    handleWorkPlaceSelect(selectedWorkPlace);
  }, [startDate]);

  useEffect(() => {
    if (
      !isEditMode &&
      activity &&
      !activities?.find(({ entityId }) => entityId === activity.entityId)
    ) {
      setValue('activity', null);
    }

    if (getCurrentlyLoggedInEmployee() && context === ViewContext.Own) {
      setValue('employee', {
        id: currentEmployee?.empId,
        entityId: currentEmployee?.entityId,
        number: currentEmployee?.nr,
        firstName: currentEmployee?.nameFirst,
        lastName: currentEmployee?.nameSurname,
        nameCompound: currentEmployee?.nameCompound,
        nameInformal: currentEmployee?.nameInformal
      });
    }
  }, [client?.id, activity?.entityId, activities]);

  useEffect(() => {
    getOrganizationalUnits();
    if (event) {
      const { h, m } = getHoursAndMinutesFromSeconds(event?.durationInSeconds);

      reset({
        isEditMode: true,
        hasProject: !!event?.project,
        schedulingLabel,
        organizationalUnit: event?.organizationalUnit
          ? event.organizationalUnit
          : defaultPlanUnit && !('costCenters' in defaultPlanUnit)
          ? defaultPlanUnit
          : undefined,
        startDate: dayjs(event?.start),
        startTime: dayjs(event?.start),
        hourDuration: h ?? 0,
        minuteDuration: m ?? 0,
        description: event?.description,
        ...(isWorkPlaceAppointment && {
          workPlace: event?.workPlace,
          activity: event?.activity ?? null,
          employee: event?.employees[0] ?? null
        }),
        ...(isProjectAppointment && {
          project: event?.project,
          activity: event?.activity ?? null,
          employee: event?.employees[0] ?? null
        }),
        ...(!isWorkPlaceAppointment &&
          !isProjectAppointment && {
            activity: event?.activity,
            client: event?.clients[0] ?? null,
            locationType: event?.location?.locationType,
            employeeType: event?.employeeType
          })
      });
    }
  }, [event?.id, defaultPlanUnit]);

  const { mutate: createAppointment, isLoading: createIsLoading } =
    mutations.useCreateAppointment();
  const { mutate: updateAppointment, isLoading: updateIsLoading } =
    mutations.useUpdateAppointment();

  function onSubmit(data: Form) {
    const {
      startDate,
      startTime,
      hourDuration,
      minuteDuration,
      activity,
      client,
      employee,
      organizationalUnit,
      editAppointmentType,
      periodType,
      interval,
      dayInWeekSet,
      recurrenceRangeType,
      description,
      locationType,
      workPlace,
      project,
      employeeType
    } = { ...defaultValues, ...data };

    const start = dayjs(startDate)
      .set('hour', startTime.hour())
      .set('minute', startTime.minute())
      .toISOString();

    const payload = {
      schedulingLabel: schedulingLabel,
      organizationalUnitId: !isProjectAppointment
        ? organizationalUnit?.id
        : null,
      activityEntityId: activity?.entityId,
      ...(!isWorkPlaceAppointment &&
        !isProjectAppointment &&
        !isEditMode && {
          description,
          ...(isRecurringChecked && {
            recurrenceRule: {
              dayInWeekSet,
              interval,
              periodType,
              recurrenceRange: {
                type: recurrenceRangeType
              }
            }
          })
        }),
      ...(!isWorkPlaceAppointment &&
        !isProjectAppointment && {
          locationType,
          employeeTypeEntityId: employeeType?.entityId
        }),
      start,
      durationInSeconds:
        (hourDuration as number) * 60 * 60 + (minuteDuration as number) * 60,
      ...(isWorkPlaceAppointment && {
        workPlaceEntityId: workPlace?.entityId
      }),
      ...(isProjectAppointment && {
        projectEntityId: project?.entityId
      }),
      description
    };

    if (event) {
      const updatePayload = {
        ...payload,
        entityId: event.id,
        appointmentGroupingEntityId:
          event.appointmentGrouping?.entityId || null,
        // NOTE: appointmentGroupingStart should be removed in backend;
        // event.start is a fallback for appointment grouping types that are not of type ROUTE.
        appointmentGroupingStart: event.scheduleStart || event.start,
        originalOccurrenceStart: event.start,
        suppressWarnings: false,
        editAppointmentType,
        employeeEntityId: employee ? employee.entityId : undefined
        // employmentPlanningEntityIdentifier: candidateEmployment
        //   ? {
        //       planningEntityType: 'EMPLOYMENT',
        //       entityId: candidateEmployment.employmentEntityId,
        //       contractNumber: candidateEmployment.employmentNumber,
        //       number: candidateEmployment.employeeNumber
        //     }
        //   : undefined
      } as UpdateAppointmentRequest;

      const modifiedUpdatePayload = appendCorrelationToken
        ? appendCorrelationToken(updatePayload)
        : updatePayload;

      updateAppointment(modifiedUpdatePayload, {
        onSuccess: (response: any) => {
          if (response && !response?.success) {
            setPayload(modifiedUpdatePayload);
            const notifications = response.notifications as APNotification[];
            const conflictingEvents: Event[] = getConflictingEvents(
              events || [],
              notifications
            );

            setDialogState({
              type: DialogType.WARNING,
              open: true,
              data: notificationsToHashTable(conflictingEvents, notifications),
              onSubmit: () => {
                if (modifiedUpdatePayload) {
                  updateAppointment(
                    {
                      ...modifiedUpdatePayload,
                      suppressWarnings: true
                    } as UpdateAppointmentRequest,
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries('planning');
                        showSnackbar({
                          severity: 'success',
                          message: t('changesSaved')
                        });
                        reset(defaultValues);
                        setPayload(null);
                        handleClose();
                      }
                    }
                  );
                }
              }
            });
            return;
          }
          queryClient.invalidateQueries('planning');

          // TODO: Uncomment once the Event object contains an employmentEntityId
          // If we have changed the employment planning entity, we want to scroll to the appointment on the plan board
          // if (candidateEmployment?.employmentEntityId !== event.employmentEntityId) {
          //   scrollToAppointment(event.id, 2000);
          // }

          showSnackbar({ severity: 'success', message: t('changesSaved') });
          reset(defaultValues);
          handleClose();
        },
        onError: () => {
          showSnackbar({ severity: 'error', message: t('somethingWentWrong') });
        }
      });

      return;
    }

    const createPayload: CreateAppointmentRequest = {
      ...payload,
      clientEntityId: client?.id,
      employeeEntityId: employee ? employee.entityId : undefined
    };

    createAppointment(createPayload, {
      onSuccess: () => {
        queryClient.invalidateQueries('planning');
        showSnackbar({ severity: 'success', message: t('changesSaved') });
        reset(defaultValues);
        handleClose();
      },
      onError: () => {
        showSnackbar({ severity: 'error', message: t('somethingWentWrong') });
      }
    });
  }

  const hasDirtiedFields: boolean =
    Object.keys(dirtyFields).filter(
      (field: string) => field !== 'editAppointmentType'
    ).length > 0;
  const hasSpecifiedRecurrence: boolean = !!dirtyFields[
    'editAppointmentType'
  ] as boolean;

  const organizationalUnit = event?.organizationalUnit
    ? event.organizationalUnit
    : selectedOrganizationalUnit &&
      !('costCenters' in selectedOrganizationalUnit)
    ? selectedOrganizationalUnit
    : undefined;

  return (
    <>
      <MultiStepForm
        onSubmit={(handleSubmit as any)(onSubmit)}
        methods={methods}
      >
        <WhereSection
          enable={hasFieldGrant('WHERE')}
          control={control}
          organizationalUnit={organizationalUnit}
          organizationalUnits={organizationalUnits}
          hasFieldGrant={hasFieldGrant}
          handleWorkPlaceSelect={handleWorkPlaceSelect}
        />

        <WhoSection
          enable={hasFieldGrant('WHO')}
          control={control}
          client={client}
          clients={clients}
          //candidateEmployment={candidateEmployment}
          employee={employee}
          event={event}
          hasFieldGrant={hasFieldGrant}
          handleEmployeeSelect={handleEmployeeSelect}
          organizationalUnit={organizationalUnit}
        />

        <WhatSection
          enable
          control={control}
          activities={activities ?? []}
          employeeType={employeeType}
          employeeTypes={employeeTypes ?? []}
          hasFieldGrant={hasFieldGrant}
          handleProjectSelect={handleProjectSelect}
        />

        <WhenSection
          enable
          control={control}
          hasFieldGrant={hasFieldGrant}
          setValue={setValue}
          event={event}
          dayInWeekSet={dayInWeekSet}
          minuteDuration={minuteDuration}
          hourDuration={hourDuration}
          periodType={periodType}
          selectedProject={selectedProject}
          selectedWorkPlace={selectedWorkPlace}
        />

        <WhichSection
          control={control}
          enable={hasFieldGrant('WHICH')}
          editAppointmentType={editAppointmentType}
          hasDirtiedFields={hasDirtiedFields}
          hasSpecifiedRecurrence={hasSpecifiedRecurrence}
        />

        <ExtraSection
          enable={hasFieldGrant('EXTRA')}
          control={control}
          hasFieldGrant={hasFieldGrant}
          locationType={locationType}
        />

        <MultiStepForm.Actions>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={2}
          >
            <Box display="flex" ml="auto">
              <LoadingButton
                loading={createIsLoading || updateIsLoading}
                type="submit"
                variant="contained"
                autoFocus
                color="dark"
                disabled={
                  isRecurring
                    ? !hasDirtiedFields || !hasSpecifiedRecurrence
                    : !hasDirtiedFields
                }
              >
                {t('save')}
              </LoadingButton>
            </Box>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
}
